import './PermissionsSettings.scss';
import { AudienceChoiceGroup } from '../AudienceChoiceGroup/AudienceChoiceGroup';
import { autobind, KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { Checkbox, ICheckbox } from 'office-ui-fabric-react/lib/Checkbox';
import { ExpirationDatePicker } from '../ExpirationDatePicker/ExpirationDatePicker';
import { PasswordField } from '../PasswordField/PasswordField';
import {
    ExpirationErrorCode,
    getExpirationErrorCode,
    getExpirationDatePickerErrorMessage,
    isCustomLabeledSpecificPeopleSupported
} from '../../../utilities/ShareHelper';
import { FileShareIconMap } from '../../../data/Icons/FileShareIconMap';
import { IAudienceChoice } from '../AudienceChoiceGroup/AudienceChoiceGroup';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import {
    ISharingInformation, ISharingLinkSettings, SharingAudience,
    SharingLinkKind, FileShareType, ISharingLinkAbilities, ClientId, SharingRole,
    IEngagementExtraData, Mode, ICustomLabeledAudienceConfig
} from '../../../interfaces/SharingInterfaces';
import { ChangePermission } from '../../PermissionsList/ShareLink/ChangePermission/ChangePermission';
import * as PeoplePickerHelper from '../../../utilities/PeoplePickerHelper';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as SharingAbilitiesHelper from '../../../utilities/SharingAbilitiesHelper';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import PeoplePicker from '../../PeoplePicker/PeoplePicker';
import stringsResx from '../../Share.resx';
import Features from '@ms/odsp-utilities/lib/features/Features';
import { PreventDownload } from '../PreventDownload/PreventDownload';
import * as ClientIdHelper from '../../../utilities/ClientIdHelper';
import { createRef } from 'office-ui-fabric-react/lib/Utilities';
import { TeachingBubble } from 'office-ui-fabric-react/lib/TeachingBubble';
import { Icon } from 'office-ui-fabric-react/lib/Icon';

// Features
const ReviewPermissionUI = { ODB: 772 };

export interface IPermissionsSettingsState {
    expirationErrorCode: ExpirationErrorCode;
    externalRecipientWarning: string;
    groupRecipientWarning: string;
    preventDownloadDisabled: boolean;
    showPickerTeachingBubble: boolean;
    showPasswordTeachingBubble: boolean;
    isSpecificPeopleReadOnlyExpanded: boolean;
}

export interface IPermissionsSettingsProps {
    companyName: string;
    clientId: ClientId;
    mode: Mode;
    currentSettings: ISharingLinkSettings;
    peoplePickerError: JSX.Element;
    onAudienceChange: (audience: SharingAudience) => void;
    onEditChange: (value: boolean) => void;
    onRoleChange: (value: SharingRole) => void;
    onExpirationChange: (expiration: Date) => void;
    onPeoplePickerChange: (items: Array<IPerson>) => void;
    selectedPermissions: ISharingLinkSettings;
    sharingInformation: ISharingInformation;
    showExistingAccessOption: boolean;
    updateExpirationErrorCode: (code: ExpirationErrorCode) => void;
    groupsMemberCount: number;
    createPeopleSharingLinks?: boolean;
    copyLinkShortcut: boolean;
    onPasswordChange: (newPassword: string) => void;
    onToggleChange: (value: boolean) => void;
    engagementExtraData: IEngagementExtraData;
    recipients: IPerson[];
    isLoadingDefaultRecipients: boolean;
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
    preferCustomLabeledAudience: boolean;
    shareId: string;
}

interface IUpsellTeachingBubbleInfo {
    targetElement: HTMLElement;
    onDismiss: (ev) => void;
    headline: string;
    mainText: string;
}

export class PermissionsSettings extends React.Component<IPermissionsSettingsProps, IPermissionsSettingsState> {

    private _permissionsOptions: Array<IAudienceChoice>;
    private _resize: () => void;
    static contextTypes = {
        resize: PropTypes.func.isRequired,
        sharingStore: PropTypes.bool.isRequired,
        goPremium: PropTypes.func,
        premiumLearnMore: PropTypes.func
    };
    private _allowEditCheckbox = createRef<ICheckbox>();
    private _disablePremiumFeatures;
    private _pickerUpsellDiv = createRef<HTMLElement>();
    private _passwordUpsellDiv = createRef<HTMLElement>();

    constructor(props: IPermissionsSettingsProps, context: any) {
        super(props);

        this._resize = context.resize;

        const selectedItems = props.selectedPermissions.specificPeople;
        this.state = {
            expirationErrorCode: ExpirationErrorCode.none,
            externalRecipientWarning: PeoplePickerHelper.getOversharingExternalsWarning(selectedItems),
            groupRecipientWarning: PeoplePickerHelper.getOversharingGroupsWarning(selectedItems, props.groupsMemberCount),
            preventDownloadDisabled: this.props.selectedPermissions.isEdit,
            showPickerTeachingBubble: false,
            showPasswordTeachingBubble: false,
            isSpecificPeopleReadOnlyExpanded: false
        };

        this._getAudienceOptions = this._getAudienceOptions.bind(this);
        this._onAllowEditChange = this._onAllowEditChange.bind(this);
        this._onAllowRoleChange = this._onAllowRoleChange.bind(this);
        this._onChoiceGroupChange = this._onChoiceGroupChange.bind(this);
        this._onSelectDate = this._onSelectDate.bind(this);
        this._renderExpirationDatePicker = this._renderExpirationDatePicker.bind(this);
        this._renderExpirationDatePickerErrorMessage = this._renderExpirationDatePickerErrorMessage.bind(this);
        this._renderPeoplePicker = this._renderPeoplePicker.bind(this);

        // Initialize permissions options.
        this._permissionsOptions = this._getAudienceChoiceGroupOptions();
    }

    public componentDidMount() {
        if (this._allowEditCheckbox.current && !this.context.sharingStore.isAudienceModificationAllowed()) {
            this._allowEditCheckbox.current.focus();
        }

        this._disablePremiumFeatures = !this.context.sharingStore.isPremiumExperienceAllowed();
        if (this._disablePremiumFeatures) {
            this._pickerUpsellDiv.current.addEventListener('click', (ev) => { this._togglePickerTeachingBubble(); });
            this._pickerUpsellDiv.current.addEventListener('keydown', (ev) => {
                if (ev.keyCode === KeyCodes.enter || ev.keyCode === KeyCodes.space) {
                    this._togglePickerTeachingBubble();
                }
            });
            this._passwordUpsellDiv.current.addEventListener('click', (ev) => { this._togglePasswordTeachingBubble(); });
            this._passwordUpsellDiv.current.addEventListener('keydown', (ev) => {
                if (ev.keyCode === KeyCodes.enter || ev.keyCode === KeyCodes.space) {
                    this._togglePasswordTeachingBubble();
                }
            });
        }
    }

    public componentDidUpdate(prevProps: IPermissionsSettingsProps, prevState: IPermissionsSettingsState) {
        this._resize();
    }

    public render(): React.ReactElement<{}> {

        const props = this.props;
        this._disablePremiumFeatures = !this.context.sharingStore.isPremiumExperienceAllowed();

        if (this.context.sharingStore.isAudienceModificationAllowed()) {
            return (
                <div className='od-PermissionsSettings'>
                    <div className='od-PermissionsSettings-section'>
                        <div className='od-PermissionsSettings-description od-ModifyPermissions-margins'>
                            { stringsResx.permissionsSettingsHeader }
                            <a className='od-PermissionsSettings-learnMore'
                                aria-label={ `${stringsResx.learnMoreLabel}: ${stringsResx.permissionsSettingsHeader}` }
                                href='https://go.microsoft.com/fwlink/?linkid=846149'
                                target='_blank'
                                onClick={ this._learnMoreClicked }>
                                { stringsResx.learnMoreLabel }
                            </a>
                        </div>
                        <div className='od-PermissionsSettings-bottomBorder' />
                        <AudienceChoiceGroup
                            items={ this._getAudienceOptions() }
                            onChange={ this._onChoiceGroupChange }
                            onAudienceChange={ props.onAudienceChange }
                            companyName={ props.companyName }
                            clientId={ props.clientId }
                        />
                        { this._renderPeoplePicker() }
                    </div>
                    { this._renderOtherSettings() }
                </div>
            );
        } else {
            return (
                <div className='od-PermissionsSettings'>
                    <div className='od-PermissionsSettings-section'>
                        <div className='od-PermissionsSettings-bottomBorder' />
                    </div>
                    { this._renderOtherSettings() }
                </div>
            );
        }
    }

    public componentWillReceiveProps(nextProps: IPermissionsSettingsProps) {
        const selectedPermissions = nextProps.selectedPermissions;
        const selectedItems = selectedPermissions.specificPeople;

        if (selectedItems.length === 0 && nextProps.recipients.length > 0) {
            this._onPeoplePickerChange(nextProps.recipients);
        }

        this.setState({
            ...this.state,
            expirationErrorCode: getExpirationErrorCode(selectedPermissions.expiration, this.props.sharingInformation.anonymousLinkExpirationRestrictionDays),
            externalRecipientWarning: PeoplePickerHelper.getOversharingExternalsWarning(selectedItems),
            groupRecipientWarning: PeoplePickerHelper.getOversharingGroupsWarning(selectedItems, nextProps.groupsMemberCount),
            preventDownloadDisabled: selectedPermissions.isEdit
        }, () => {
            this.props.updateExpirationErrorCode(this.state.expirationErrorCode);
        });
    }

    private _togglePickerTeachingBubble() {
        this.setState({ showPickerTeachingBubble: !this.state.showPickerTeachingBubble });
    }

    private _togglePasswordTeachingBubble() {
        this.setState({ showPasswordTeachingBubble: !this.state.showPasswordTeachingBubble });
    }

    @autobind
    private _learnMoreClicked() {
        this.context.sharingStore.getEngagementHelper().linkSettingsLearnMoreClicked(this.props.engagementExtraData);
    }

    private _renderOtherSettings() {

        if (this.props.selectedPermissions.audience !== SharingAudience.existing) {
            return (
                <div className='od-PermissionsSettings-section od-ModifyPermissions-margins'>
                    <div className='od-PermissionsSettings-description'>{ this.context.sharingStore.isAudienceModificationAllowed() ? stringsResx.otherSettings : '' }</div>
                    <div role='group' aria-label={ stringsResx.otherSettings }>
                        <div className='od-PermissionsSettings-setting'>
                            { this._renderRolesDropDown() }
                        </div>
                        { this._renderExpirationDatePicker() }
                        { this._renderPasswordField() }
                        { this._renderPreventDownloadToggle() }
                    </div>
                </div>
            );
        }
    }

    @autobind
    private _getSpecificPeopleOption() {

        if (this.props.createPeopleSharingLinks) {
            return {
                disabledText: stringsResx.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.specificPeople],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.specificPeopleFlex,
                label: stringsResx.permissionsSpecificPeopleString,
                linkKinds: [SharingLinkKind.flexible],
                permissionsType: FileShareType.specificPeople
            };
        } else {
            return {
                disabledText: stringsResx.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.specificPeople],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.specificPeople,
                label: stringsResx.permissionsSpecificPeopleString,
                linkKinds: [SharingLinkKind.direct],
                permissionsType: FileShareType.specificPeople
            };
        }
    }

    private _getAudienceChoiceGroupOptions(): Array<IAudienceChoice> {

        const options: Array<IAudienceChoice> = [
            {
                disabledText: stringsResx.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.anyone],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.anyone,
                label: stringsResx.permissionsAnyoneString,
                linkKinds: [SharingLinkKind.anonymousView, SharingLinkKind.anonymousEdit],
                permissionsType: FileShareType.anyone
            },
            {
                disabledText: stringsResx.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.workGroup],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.organization,
                label: StringHelper.format(stringsResx.permissionsCompanyString, this.props.companyName),
                linkKinds: [SharingLinkKind.organizationView, SharingLinkKind.organizationEdit],
                permissionsType: FileShareType.workGroup
            },
            this._getSpecificPeopleOption()
        ];

        /**
         * Show this additional option if the user has no permissions to add new users
         * to the permissions list or if we're in SharePoint team sites.
         */
        if (this.props.showExistingAccessOption || !this.props.sharingInformation.canManagePermissions || this.props.sharingInformation.blockPeoplePickerAndSharing) {
            const existingPeopleOption: IAudienceChoice = {
                icon: FileShareIconMap[FileShareType.workGroup],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.existing,
                label: stringsResx.permissionsExistingPeopleString,
                linkKinds: [SharingLinkKind.direct],
                permissionsType: FileShareType.existing
            };

            options.splice(2, 0, existingPeopleOption);
        }

        return options;
    }

    @autobind
    private _onPeoplePickerChange(items: Array<IPerson>) {
        this.setState({
            ...this.state,
            externalRecipientWarning: PeoplePickerHelper.getOversharingExternalsWarning(items),
            groupRecipientWarning: PeoplePickerHelper.getOversharingGroupsWarning(items, this.props.groupsMemberCount)
        }, () => {
            this.props.onPeoplePickerChange(items);
        });
    }

    private _renderPeoplePicker() {
        const props = this.props;
        const state = this.state;

        if ((props.selectedPermissions.audience === SharingAudience.specificPeople ||
            props.selectedPermissions.audience === SharingAudience.specificPeopleFlex) &&
            (props.copyLinkShortcut || props.mode === Mode.linkSettings)) {

            if (ClientIdHelper.isOutlook(this.props.clientId)) {
                return (
                    <div className="od-PermissionsSetting-specificPeople-container">
                        { props.currentSettings.specificPeople.length > 0 ? this._renderReadOnlySpecificPeople() : undefined }
                    </div>
                );
            } else {
                return (
                    <div className='od-ModifyPermissions-margins od-PermissionsSettings-PeoplePicker'>
                        <PeoplePicker
                            defaultSelectedItems={ props.selectedPermissions.specificPeople }
                            onChange={ this._onPeoplePickerChange }
                            oversharingExternalsWarning={ state.externalRecipientWarning }
                            oversharingGroupsWarning={ state.groupRecipientWarning }
                            pickerSettings={ props.sharingInformation.peoplePickerSettings }
                            error={ props.peoplePickerError }
                            sharingInformation={ props.sharingInformation }
                            clientId={ this.props.clientId }
                            onResolvedSuggestions={ this._onResolvedSuggestions }
                        />
                    </div>
                );
            }
        } else {
            return;
        }
    }
    @autobind
    private _renderReadOnlySpecificPeople() {
        let topPeople = '';
        const specificPeople = this.props.currentSettings.specificPeople;
        const maxPeople = this.state.isSpecificPeopleReadOnlyExpanded ? specificPeople.length : 5;
        const additionalPeopleCount = specificPeople.length - maxPeople;

        for (let i = 0; i < Math.min(specificPeople.length, maxPeople); i++) {
            topPeople += specificPeople[i].name + (specificPeople.length - 1 > i ? ', ' : '');
        }
        const additionalPeopleLink = additionalPeopleCount > 0 && !this.state.isSpecificPeopleReadOnlyExpanded ?
            <a href="javascript:void(0)" onClick={ this._setSpecificPeopleReadOnlyExpanded }>{ ' +' + additionalPeopleCount }</a> :
            undefined;

        return (
            <div className='od-PermissionsSetting-specificPeople-list'>
                { topPeople }
                { additionalPeopleLink }
            </div >
        );
    }

    @autobind
    private _setSpecificPeopleReadOnlyExpanded() {
        this.setState({ isSpecificPeopleReadOnlyExpanded: true });
    }

    private _renderRolesDropDown() {
        const props = this.props;
        const showReview = Features.isFeatureEnabled(ReviewPermissionUI);
        const doesClientSupportReview = (props.clientId === ClientId.word) || (props.sharingInformation.item &&
            props.sharingInformation.item.appmap && props.sharingInformation.item.appmap === 'ms-word');
        const isSpecificPeopleSelected = props.selectedPermissions.audience === SharingAudience.specificPeople ||
            props.selectedPermissions.audience === SharingAudience.specificPeopleFlex;
        if (showReview && doesClientSupportReview && isSpecificPeopleSelected) {
            return (
                <div className='od-PermissionsSettings-changePermission'>
                    <ChangePermission
                        selectedPermissions={ props.selectedPermissions }
                        onRoleChange={ this._onAllowRoleChange }
                        clientId={ props.clientId }
                        appmap={ props.sharingInformation.item.appmap }
                    />
                </div>
            );
        } else {
            return (
                <Checkbox
                    className='od-PermissionsSettings-allowEdit'
                    label={ stringsResx.allowEditLabel }
                    onChange={ this._onAllowEditChange }
                    checked={ props.selectedPermissions.isEdit }
                    disabled={ !props.selectedPermissions.allowEditing }
                    componentRef={ this._allowEditCheckbox }
                />
            );
        }
    }

    /**
     * Render ExpirationDatePicker component if audience is 'Anonymous',
     * otherwise don't render anything.
     */
    private _renderExpirationDatePicker() {
        const props = this.props;
        const selectedPermissions = props.selectedPermissions;
        const isExpirationDatesEnabled = this.context.sharingStore.isExpirationDateModificationAllowed();

        if (isExpirationDatesEnabled && selectedPermissions.audience === SharingAudience.anyone) {
            if (this._disablePremiumFeatures) {
                return (
                    <div ref={ this._pickerUpsellDiv } className='od-PermissionsSettings-wrapper' tabIndex={ 0 }>
                        <div className='od-PermissionsSettings-blocker' aria-hidden='true'>
                            <div className='od-PermissionsSettings-setting'>
                                <div className='od-PermissionsSettings-datePicker'>
                                    <ExpirationDatePicker
                                        clientId={ this.props.clientId }
                                        onSelectDate={ this._onSelectDate }
                                        value={ selectedPermissions.expiration }
                                        expiryRestriction={ props.sharingInformation.anonymousLinkExpirationRestrictionDays }
                                        disableAutoFocus={ this._disablePremiumFeatures }
                                        hideRemoveDateButton={ this._disablePremiumFeatures }
                                        showGleam={ this._disablePremiumFeatures }
                                        tabIndex={ -1 }
                                    />
                                </div>
                                { this._renderExpirationTeachingBubble() }
                                <span className='od-PermissionsSettings-expirationError' role='alert' aria-live='assertive'>
                                    { this._renderExpirationDatePickerErrorMessage() }
                                </span>
                            </div>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className='od-PermissionsSettings-setting'>
                        <div className='od-PermissionsSettings-datePicker'>
                            <ExpirationDatePicker
                                clientId={ this.props.clientId }
                                onSelectDate={ this._onSelectDate }
                                value={ selectedPermissions.expiration }
                                expiryRestriction={ props.sharingInformation.anonymousLinkExpirationRestrictionDays }
                                disableAutoFocus={ this._disablePremiumFeatures }
                                hideRemoveDateButton={ this._disablePremiumFeatures }
                                showGleam={ this._disablePremiumFeatures }
                            />
                        </div>
                        { this._renderExpirationTeachingBubble() }
                        <span className='od-PermissionsSettings-expirationError' role='alert' aria-live='assertive'>
                            { this._renderExpirationDatePickerErrorMessage() }
                        </span>
                    </div>
                );
            }
        } else {
            return;
        }
    }

    private _renderExpirationTeachingBubble() {
        if (this.state.showPickerTeachingBubble) {
            const expirationBubbleInfo: IUpsellTeachingBubbleInfo = {
                targetElement: this._pickerUpsellDiv.current,
                onDismiss: (ev) => this.setState({ showPickerTeachingBubble: false }),
                headline: stringsResx.expirationTeachingBubbleHeadline,
                mainText: stringsResx.expirationTeachingBubbleText
            };
            return this._renderUpsellTeachingBubble(expirationBubbleInfo);
        }
    }

    private _renderExpirationDatePickerErrorMessage() {
        const expirationErrorCode = this.state.expirationErrorCode;
        return getExpirationDatePickerErrorMessage(expirationErrorCode, this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);
    }

    /**
    * Render PasswordField component if audience is 'Anonymous',
    * otherwise don't render anything.
    */
    private _renderPasswordField() {
        const props = this.props;
        const isPasswordProtectedLinksEnabled = this.context.sharingStore.isPasswordModificationAllowed();
        const selectedPermissions = props.selectedPermissions;
        if (isPasswordProtectedLinksEnabled && selectedPermissions.audience === SharingAudience.anyone) {
            if (this._disablePremiumFeatures) {
                return (
                    <div ref={ this._passwordUpsellDiv } className='od-PermissionsSettings-wrapper' tabIndex={ 0 }>
                        <div className='od-PermissionsSettings-blocker' aria-hidden='true'>
                            <div className='od-PermissionsSettings-setting'>
                                <PasswordField
                                    onPasswordChange={ this.props.onPasswordChange }
                                    readOnly={ true }
                                    showGleam={ true }
                                    tabIndex={ -1 }
                                />
                                { this._renderPasswordTeachingBubble() }
                            </div>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className='od-PermissionsSettings-setting'>
                        <PasswordField
                            onPasswordChange={ this.props.onPasswordChange }
                        />
                    </div>
                );
            }
        } else {
            return;
        }
    }

    private _renderPasswordTeachingBubble() {
        if (this.state.showPasswordTeachingBubble) {
            const passwordBubbleInfo: IUpsellTeachingBubbleInfo = {
                targetElement: this._passwordUpsellDiv.current,
                onDismiss: (ev) => this.setState({ showPasswordTeachingBubble: false }),
                headline: stringsResx.passwordTeachingBubbleHeader,
                mainText: stringsResx.passwordTeachingBubbleText
            };
            return this._renderUpsellTeachingBubble(passwordBubbleInfo);
        }
    }

    /**
     * Gets the audience options in the "Link Settings" page. If
     * a value is set in the React context, we may use a new property
     * from the GetSharingInformation response (sharingAbilities).
     */
    private _getAudienceOptions() {
        const sharingAbilities = this.props.sharingInformation.sharingAbilities;
        const sharingInformation = this.props.sharingInformation;
        const viableAudienceOptions = [];
        const currentSettings = this.props.currentSettings;
        let specificPeopleAudienceOption = undefined;

        for (const linkType in sharingAbilities) {
            if (sharingAbilities.hasOwnProperty(linkType)) {
                const abilities: ISharingLinkAbilities = sharingAbilities[linkType];
                const audience = SharingAbilitiesHelper.getSharingAbilitiesAudience(linkType);

                /**
                 * If the key doesn't map to a link type, then bail because
                 * it's the __metadata property from the server response.
                 *
                 * If we're processing a PSL, ensure that the flight is enabled
                 * (using the createPeopleSharingLinks prop). If it's not enabled,
                 * then add legacy "Specific people" option instead.
                 */
                if (!audience) {
                    continue;

                } else if (audience === SharingAudience.specificPeopleFlex && !this.props.createPeopleSharingLinks) {
                    specificPeopleAudienceOption = this._getSpecificPeopleOption();

                    if (sharingInformation.canManagePermissions && !sharingInformation.blockPeoplePickerAndSharing) {
                        specificPeopleAudienceOption.isChecked = (specificPeopleAudienceOption.key === currentSettings.audience ||
                            SharingAudience.specificPeopleFlex === currentSettings.audience);
                    } else {
                        specificPeopleAudienceOption.isDisabled = true;
                    }
                    viableAudienceOptions.push(specificPeopleAudienceOption);
                    continue;
                }

                let audienceOption = this._getAudienceOption(audience);

                // If user can't manage an edit or a view link, disable the option.
                audienceOption.isDisabled = !(abilities.canManageEditLink.enabled || abilities.canManageReadLink.enabled);

                // Select the audience if it's the currently selected option.
                audienceOption.isChecked = audienceOption.key === currentSettings.audience;

                viableAudienceOptions.push(audienceOption);
            }
        }

        // Add "Existing people" option.
        const existingPeopleOption: IAudienceChoice = {
            icon: FileShareIconMap[FileShareType.workGroup],
            isChecked: currentSettings.audience === SharingAudience.existing,
            isDisabled: false,
            key: SharingAudience.existing,
            label: stringsResx.permissionsExistingPeopleString,
            linkKinds: [SharingLinkKind.direct],
            permissionsType: FileShareType.existing
        };
        viableAudienceOptions.splice(2, 0, existingPeopleOption);

        // Add "People in this chat" option.
        let specificPeopleOption = viableAudienceOptions.filter((option: { key: SharingAudience; isDisabled: boolean }) => {
            return (option.key === SharingAudience.specificPeople && !option.isDisabled ||
                option.key === SharingAudience.specificPeopleFlex && !option.isDisabled) ? option : null;
        }).length > 0;
        const addCustomOption = isCustomLabeledSpecificPeopleSupported(
            this.props.clientId,
            this.props.mode,
            this.props.customLabeledAudienceConfig,
            (currentSettings.audience === SharingAudience.customLabeledSpecificPeople || (this.props.recipients.length > 0 || this.props.isLoadingDefaultRecipients)));
        if (!!specificPeopleOption && addCustomOption) {
            let customLabeledSpecificPeopleOption = this._getAudienceOption(SharingAudience.customLabeledSpecificPeople);
            viableAudienceOptions.splice(2, 0, customLabeledSpecificPeopleOption);
            customLabeledSpecificPeopleOption.isDisabled = !(this.props.sharingInformation.canAddInternalPrincipal || this.props.sharingInformation.canAddExternalPrincipal);
            customLabeledSpecificPeopleOption.isChecked = currentSettings.audience === SharingAudience.customLabeledSpecificPeople ||
                (this.props.preferCustomLabeledAudience && currentSettings.audience === SharingAudience.specificPeopleFlex);
        }

        if (this.props.mode === Mode.linkSettings &&
            ClientIdHelper.isOutlook(this.props.clientId) &&
            (!this.props.shareId || (currentSettings.audience !== SharingAudience.specificPeopleFlex)) &&
            !!specificPeopleOption) {
            // Do not show specific people link for Outlook link settings mode when a shareId has not
            // been passed, or one is passed but it is not for a PSL
            viableAudienceOptions.splice(viableAudienceOptions.indexOf(specificPeopleOption), 1);
        }

        return viableAudienceOptions;
    }

    private _renderPreventDownloadToggle() {
        const selectedPermissions = this.props.selectedPermissions;
        const isToggledValue = !!selectedPermissions.preventDownload;
        const isPreventDownloadEnabled = this.context.sharingStore.isPreventDownloadAllowed();

        // Don't show the prevent download toggle for now if navId is specified since it kicks in
        // the old office viewer that does not support deep linking.
        const isNavIdAvailable = !!this.context.sharingStore.getNavId()

        // Only if file is word, excel, or ppt, render PreventDownloadToggle.
        if (!isNavIdAvailable && isPreventDownloadEnabled && SharingAbilitiesHelper.supportsRestrictedView(selectedPermissions.audience, this.props.sharingInformation.sharingAbilities)) {
            return (
                <div className="od-PermissionsSettings-setting">
                    <div className='od-PermissionsSettings-setting--PreventDownload'>
                        <PreventDownload
                            onToggleChange={ this.props.onToggleChange }
                            isToggled={ isToggledValue }
                            clientId={ this.props.clientId }
                            mode={ this.props.mode }
                            disablePreventDownload={ this.state.preventDownloadDisabled }
                        />
                    </div>
                </div >
            );
        } else {
            return;
        }
    }

    private _renderUpsellTeachingBubble(info: IUpsellTeachingBubbleInfo) {
        let upgradeFunction: () => void = () => window.open('https://onedrive.live.com/about/plans/');
        let secondaryButtonProps;
        if (this.context.goPremium) {
            upgradeFunction = this.context.goPremium;
        } else {
            try {
                const externalJavaScript: any = window.external;
                if (externalJavaScript.GoPremium && externalJavaScript.IsGoPremiumAvailable && externalJavaScript.IsGoPremiumAvailable()) {
                    upgradeFunction = () => { externalJavaScript.GoPremium('https://onedrive.live.com/about/plans/'); };
                }
            } catch (error) {
                // If window.external isn't defined, don't override the function
            }
        }
        if (this.context.premiumLearnMore) {
            secondaryButtonProps = { children: stringsResx.teachingBubbleLearnMoreButton, onClick: this.context.premiumLearnMore };
        } else {
            try {
                const externalJavaScript: any = window.external;
                if (externalJavaScript.PremiumLearnMore && externalJavaScript.IsPremiumLearnMoreAvailable && externalJavaScript.IsPremiumLearnMoreAvailable()) {
                    secondaryButtonProps = { children: stringsResx.teachingBubbleLearnMoreButton, onClick: () => { externalJavaScript.PremiumLearnMore(); } };
                }
            } catch (error) {
                // If window.external isn't defined, don't show the learn more button
            }
        }
        return (
            <TeachingBubble
                targetElement={ info.targetElement }
                onDismiss={ info.onDismiss }
                hasCloseIcon={ true }
                headline={ info.headline }
                primaryButtonProps={ { children: stringsResx.teachingBubbleUpgradeButton, onClick: upgradeFunction } }
                secondaryButtonProps={ secondaryButtonProps }
                calloutProps={ { className: 'od-PermissionsSettings-teachingBubble' } }
                hasSmallHeadline={ true }
            >
                <Icon iconName='Diamond' className='od-PermissionsSettings-teachingBubbleIcon' />
                { info.mainText }
            </TeachingBubble>
        );
    }

    /**
     * Gets the option template for the given audience.
     *
     * @param audience The sharing audience to get option for.
     */
    @autobind
    private _getAudienceOption(audience: SharingAudience) {
        const strings = stringsResx;

        if (audience === SharingAudience.anyone) {
            return {
                disabledText: strings.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.anyone],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.anyone,
                label: stringsResx.permissionsAnyoneString,
                linkKinds: [SharingLinkKind.anonymousView, SharingLinkKind.anonymousEdit],
                permissionsType: FileShareType.anyone
            };
        } else if (audience === SharingAudience.organization) {
            return {
                disabledText: strings.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.workGroup],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.organization,
                label: StringHelper.format(stringsResx.permissionsCompanyString, this.props.companyName),
                linkKinds: [SharingLinkKind.organizationView, SharingLinkKind.organizationEdit],
                permissionsType: FileShareType.workGroup
            };
        } else if (audience === SharingAudience.specificPeopleFlex) {
            return {
                disabledText: strings.disabledAudienceChoiceLabel2,
                icon: FileShareIconMap[FileShareType.specificPeople],
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.specificPeopleFlex,
                label: strings.permissionsSpecificPeopleString,
                linkKinds: [SharingLinkKind.flexible],
                permissionsType: FileShareType.specificPeople
            };
        } else if (audience === SharingAudience.customLabeledSpecificPeople) {
            const isOutlook = ClientIdHelper.isOutlook(this.props.clientId);
            const fileType = isOutlook ? FileShareType.mail : FileShareType.chat;
            let defaultLabel = '';
            let audienceType = this.props.customLabeledAudienceConfig && this.props.customLabeledAudienceConfig.audienceType;
            if (isOutlook || audienceType === 'recipient') {
                defaultLabel = strings.permissionsRecipientsOfThisMessageString;
            } else if (audienceType === 'channel') {
                defaultLabel = strings.permissionsPeopleInChannelString;
            } else if (audienceType === 'team') {
                defaultLabel = strings.permissionsPeopleinTeamString;
            } else {
                defaultLabel = strings.permissionsPeopleInChatString;
            }
            return {
                disabledText: stringsResx.disabledAudienceChoiceLabel2,
                icon: !(this.props.customLabeledAudienceConfig && this.props.customLabeledAudienceConfig.iconUrl) && FileShareIconMap[fileType],
                iconUrl: this.props.customLabeledAudienceConfig && this.props.customLabeledAudienceConfig.iconUrl,
                isChecked: false,
                isDisabled: false,
                key: SharingAudience.customLabeledSpecificPeople,
                label: this.props.customLabeledAudienceConfig && !!this.props.customLabeledAudienceConfig.entityName ?
                    StringHelper.format(strings.permissionsOnlyCustomLabeledString, this.props.customLabeledAudienceConfig.entityName) :
                    defaultLabel,
                linkKinds: [SharingLinkKind.flexible],
                permissionsType: !(this.props.customLabeledAudienceConfig && this.props.customLabeledAudienceConfig.iconUrl) && fileType
            };
        }

        return undefined;
    }

    private _onChoiceGroupChange(key: SharingAudience) {
        for (const option of this._permissionsOptions) {
            option.isChecked = key === option.key;
        }
    }

    private _onSelectDate(date: Date): void {
        const expirationErrorCode: ExpirationErrorCode = getExpirationErrorCode(date, this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);

        this.setState({
            ...this.state,
            expirationErrorCode: expirationErrorCode
        }, () => {
            this.props.updateExpirationErrorCode(expirationErrorCode);
            this.props.onExpirationChange(date);
        });
    }

    private _onAllowEditChange(evt: React.SyntheticEvent<{}>, value: boolean): void {
        this.props.onEditChange(value);

        this.setState({
            ...this.state,
            preventDownloadDisabled: value
        });
    }

    @autobind
    private _onAllowRoleChange(value): void {
        let isEdit = false;
        if ((value as SharingRole) === SharingRole.edit) {
            isEdit = true;
        }

        this.setState({
            ...this.state,
            preventDownloadDisabled: isEdit // If isEdit is true, disable prevent download link.
        }, () => {
            this.props.onRoleChange(value as SharingRole);
        });
    }

    @autobind
    private _onResolvedSuggestions(suggestions: IPerson[]): IPerson[] {
        this.context.sharingStore.getEngagementHelper().peoplePickerLinkedinSuggestions(this.props.engagementExtraData, suggestions);
        return suggestions;
    }
}
